import InformationalDialog from 'components/dialogs/InformationalDialog';
import TextInput from 'components/form/TextInput';
import TextAreaInput from 'components/form/TextAreaInput';
import {
  AuLicenseesCountry,
  EUCountries,
  CurrencyType,
  Role,
  GLOBAL_OPTION,
  EU_COUNTRIES_NAMES,
  VAT_VALIDATION_SERVICE_LINK,
} from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Field, Formik, FormikHelpers, useFormikContext } from 'formik';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import CountriesService from 'services/CountriesService';
import { CompanySize, CountryModel, RegionModel, CreateCompanyModel, CurrencySelectModel } from 'services/EpdClient';
import styled, { useTheme } from 'styled-components';
import {
  A,
  ButtonSmall,
  Container,
  ErrorText,
  FormMode,
  StyledForm,
  StyledReactSelect,
  StyledLink,
  SuccessText,
  Text,
  Label,
  HyperLink,
} from 'styles/Styles.styled';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import { FieldPanel, GrayHR } from '../../styles/v2/Styles.styled';
import { Theme } from 'styles/theme';
import { YES_NO, YesNoDataSource } from '../../components/v2/compiler/constants';
import { Option } from 'types/types';
import AvailableCountryCurrencies from '../../components/company/AvailableCountryCurrencies';
import { useDictionaryValues } from 'services/api/queries';
import calculateCurrencyOptions from 'components/company/calculateCurrencyOptions';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { registerCompanySelectStyles } from 'components/form/EditSelectStyles';
import { DropdownIndicator } from 'components/form/DropdownIndicator';
import VatValidationService from 'services/VatValidationService';
import { getRegexVATPattern } from 'components/company/getRegexVATPattern';
import TooltipHelpIcon from '../../components/v2/icons/TooltipHelpIcon';
import AvailableCountryRegions from 'components/company/AvailableCountryRegions';
import getAvailableRegionsForCountryOptions from 'components/company/getAvailableRegionsForCountryOptions';
import {
  createAllowedSymbolsTextInputProps,
  ALLOWED_SYMBOLS_ERROR,
  allowedSymbolsLink,
  allowedGeneralCharsRegex,
  emailSmartValidation,
  websiteSmartValidation,
} from '../../util/allowedSymbolsValidationUtils';
import { toast } from 'react-toastify';

type ExtendedCreateCompanyModel = CreateCompanyModel &
  CurrencySelectModel & {
    marketplace: Option[] | null;
    roleOption: Option;
    companySizeOption?: Option;
    countryOption: Option | null;
    isInvoiceUniquePurchaseOrderNumberOption: Option | null;
    isSpecialInvoiceNeedsOption: Option | null;
    regionOption: Option | null;
    invoiceRegionOption: Option | null;
  };

const DUPLICATE_ERRORS = {
  COMPANY_NAME: 'Duplicate organisation name.',
};

const DUPLICATE_UI_MESSAGES = {
  COMPANY_NAME: 'An organisation with this name already exists. Please use a different name.',
};

type DuplicateFieldKey = 'name';

const duplicateErrorMap: Record<string, { field: DuplicateFieldKey; toastLabel: string; uiMessage: string }> = {
  [DUPLICATE_ERRORS.COMPANY_NAME]: { field: 'name', toastLabel: 'name', uiMessage: DUPLICATE_UI_MESSAGES.COMPANY_NAME },
};

const isDuplicateNameError = (err?: string) => err === DUPLICATE_UI_MESSAGES.COMPANY_NAME;

const ZIP_LENGTH_ERROR = 'Please enter a ZIP code between 3 and 20 characters long.';

const RegisterCompany: FunctionComponent = () => {
  const theme = useTheme() as Theme;

  const { company, licensee, changeCompany } = useContext(CompanyContext);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [showCreatedLicenseeCompanyDialog, setShowCreatedLicenseeCompanyDialog] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>();
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [regions, setRegions] = useState<RegionModel[]>([]);
  const [countriesDs, setCountriesDs] = useState<Option[]>([]);
  const [isGlobalMarketplaceSelected, setIsGlobalMarketplaceSelected] = useState(false);
  const companyTypes = [
    { value: Role.EPDOwner, label: 'EPD Owner' },
    { value: Role.EPDDeveloper, label: 'LCA Practitioner' },
  ] as Option[];
  const glnValidationRegex = /^\d{13}$/;
  const { t } = useTranslation();
  const eurCurrencyCountries = useDictionaryValues(EPD_DICTIONARY_CODES.EUR_CURRENCY_COUNTRIES);

  const createInitialValues: ExtendedCreateCompanyModel = {
    name: '',
    roleId: Role.EPDOwner,
    website: '',
    countryId: '',
    vat: '',
    organizationEmail: '',
    isOnlyEmail: false,
    addressLine: '',
    addressLineSecond: '',
    addressCity: '',
    addressZipCode: '',
    isCompanyAddressUsedForInvoice: false,
    invoiceOrganizationName: '',
    invoiceCountryId: '',
    invoiceAddressLine: '',
    invoiceAddressLineSecond: '',
    invoiceAddressCity: '',
    invoiceAddressZipCode: '',
    invoiceEmail: '',
    invoiceCcEmail: '',
    ordersEmail: '',
    remindersEmail: '',
    isInvoiceUniquePurchaseOrderNumber: YES_NO.NO,
    isSpecialInvoiceNeeds: YES_NO.NO,
    specialInvoiceNeeds: '',
    invoiceReference: '',
    isGlobalMarketplace: false,
    marketplace: [],
    preferredCurrency: null,
    gln: '',
    abn: '',
    gst: '',
    roleOption: { value: Role.EPDOwner, label: companyTypes.find((x) => x.value == Role.EPDOwner)?.label ?? '' } as Option,
    countryOption: null,
    preferredCurrencyOption: null,
    invoiceCountryOption: null,
    isInvoiceUniquePurchaseOrderNumberOption: {
      value: YES_NO.NO,
      label: YesNoDataSource.find((x) => x.value == YES_NO.NO)?.label ?? '',
    },
    isSpecialInvoiceNeedsOption: { value: YES_NO.NO, label: YesNoDataSource.find((x) => x.value == YES_NO.NO)?.label ?? '' },
    regionId: null,
    invoiceRegionId: null,
    invoiceRegionOption: null,
    regionOption: null,
  };

  const REQUIRED_MSG = t('messages.required');
  const allowedSymbolsTextInputProps = createAllowedSymbolsTextInputProps({
    theme,
    requiredMsg: REQUIRED_MSG,
    allowedSymbolsLink,
  });

  const allowedSymbolsTextInputPropsForName = {
    ...allowedSymbolsTextInputProps,

    shouldHighlightBorder: (err: string) =>
      allowedSymbolsTextInputProps.shouldHighlightBorder?.(err) || isDuplicateNameError(err),
    shouldShowBottomError: (err: string) =>
      allowedSymbolsTextInputProps.shouldShowBottomError?.(err) || isDuplicateNameError(err),

    shouldShowTooltipError: (err: string) => {
      const base = allowedSymbolsTextInputProps.shouldShowTooltipError
        ? allowedSymbolsTextInputProps.shouldShowTooltipError(err)
        : true;

      return base && !isDuplicateNameError(err);
    },
  };

  const INVALID_URL_MSG = t('messages.invalidUrl');
  const allowedSymbolsTextInputPropsForWebsite = {
    ...allowedSymbolsTextInputProps,

    shouldShowTooltipError: (err: string) => err === REQUIRED_MSG,

    shouldHighlightBorder: (err: string) =>
      (allowedSymbolsTextInputProps.shouldHighlightBorder
        ? allowedSymbolsTextInputProps.shouldHighlightBorder(err)
        : false) || err === INVALID_URL_MSG,

    shouldShowBottomError: (err: string) =>
      (allowedSymbolsTextInputProps.shouldShowBottomError
        ? allowedSymbolsTextInputProps.shouldShowBottomError(err)
        : false) || err === INVALID_URL_MSG,
  };

  const INVALID_EMAIL_MSG = t('messages.invalidEmail');
  const SAME_CC_MSG = t('messages.ccInvoiceEmailIsTheSameAsInvoiceEmail');
  const allowedSymbolsTextInputPropsForEmail = {
    ...allowedSymbolsTextInputProps,
    shouldShowTooltipError: (err: string) => err === REQUIRED_MSG,
    shouldHighlightBorder: (err: string) =>
      (allowedSymbolsTextInputProps.shouldHighlightBorder
        ? allowedSymbolsTextInputProps.shouldHighlightBorder(err)
        : false) ||
      err === INVALID_EMAIL_MSG ||
      err === SAME_CC_MSG,

    shouldShowBottomError: (err: string) =>
      (allowedSymbolsTextInputProps.shouldShowBottomError
        ? allowedSymbolsTextInputProps.shouldShowBottomError(err)
        : false) ||
      err === INVALID_EMAIL_MSG ||
      err === SAME_CC_MSG,
  };

  const allowedSymbolsTextInputPropsForZip = {
    ...allowedSymbolsTextInputProps,

    shouldHighlightBorder: (err: string) =>
      allowedSymbolsTextInputProps.shouldHighlightBorder?.(err) || err === ZIP_LENGTH_ERROR,
    shouldShowBottomError: (err: string) =>
      allowedSymbolsTextInputProps.shouldShowBottomError?.(err) || err === ZIP_LENGTH_ERROR,
  };

  const createSchema = Yup.object({
    name: Yup.string().required(t('messages.required')).matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    roleOption: Yup.object<Option>().required(t('messages.required')),
    website: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) => websiteSmartValidation(t).required(t('messages.required')),
      otherwise: (s: any) => websiteSmartValidation(t).notRequired().nullable(),
    }),
    countryOption: Yup.object<Option>().required(t('messages.required')),
    addressLine: Yup.string().required(t('messages.required')).matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    addressLineSecond: Yup.string().matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    addressCity: Yup.string().required(t('messages.required')).matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    addressZipCode: Yup.string()
      .required(t('messages.required'))
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .transform((value) => value?.trim())
      .min(3, ZIP_LENGTH_ERROR)
      .max(20, ZIP_LENGTH_ERROR),

    vat: Yup.mixed().when(['invoiceCountryOption', 'roleOption', 'invoiceRegionOption'], {
      is: (invoiceCountryOption: Option | null, roleOption: Option | null, invoiceRegionOption: Option | null) => {
        if (roleOption?.value !== Role.EPDOwner || !invoiceCountryOption?.value) return false;
        const country = countries.find((x) => x.id === invoiceCountryOption.value);
        const region = regions.find((x) => x.id === invoiceRegionOption?.value);
        return EU_COUNTRIES_NAMES.includes(country?.name || '') || EU_COUNTRIES_NAMES.includes(region?.name || '');
      },
      then: Yup.string()
        .required(t('messages.requiredVatInEU'))
        .test('vat-format', 'vat-format-error', function (value) {
          if (!value) return true;

          const { invoiceCountryOption, invoiceRegionOption } = this.parent;
          const country = countries.find((x) => x.id === invoiceCountryOption?.value);
          const region = regions.find((x) => x.id === invoiceRegionOption?.value);

          if (!country || !country.name) return true;

          if (region?.name === 'Northern Ireland') {
            const ukVatPattern = getRegexVATPattern(region.name);
            return ukVatPattern?.test(value) ?? true;
          }

          const vatPattern = getRegexVATPattern(country.name);
          return vatPattern?.test(value) ?? true;
        })
        .test('vat-validation', 'vat-validation-error', async function (value) {
          if (!value) return false;

          const { invoiceCountryOption, invoiceRegionOption } = this.parent;
          const country = countries.find((x) => x.id === invoiceCountryOption?.value);
          const region = regions.find((x) => x.id === invoiceRegionOption?.value);

          if (!country || !country.name) return false;

          if (region?.name === 'Northern Ireland') {
            const niVatPattern = getRegexVATPattern(region.name);
            if (niVatPattern && !niVatPattern.test(value)) {
              return true;
            }
          } else {
            const vatPattern = getRegexVATPattern(country.name);
            if (vatPattern && !vatPattern.test(value)) {
              return true;
            }

            // Why do we have this validation here? It seems redundant with the previous check.
            if (!EU_COUNTRIES_NAMES.includes(country.name)) {
              return true;
            }
          }

          try {
            const result = await VatValidationService.get(value);
            return result.validationStatus !== 'InvalidVat';
          } catch (error) {
            console.warn('VAT validation service error:', error);
            return true;
          }
        }),
    }),
    companySizeOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    organizationEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) => emailSmartValidation(t).required(t('messages.required')),
      otherwise: (s: any) => emailSmartValidation(t).notRequired().nullable(),
    }),
    invoiceOrganizationName: (Yup.string() as any)
      .when('roleOption', {
        is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
        then: Yup.string().required(t('messages.required')),
      })
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    invoiceAddressLine: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when('roleOption', {
        is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
        then: Yup.string().required(t('messages.required')),
      }),
    invoiceAddressLineSecond: Yup.string().matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    invoiceAddressCity: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when('roleOption', {
        is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
        then: Yup.string().required(t('messages.required')),
      }),
    invoiceAddressZipCode: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .transform((value) => value?.trim())
      .when('roleOption', {
        is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
        then: Yup.string().required(t('messages.required')).min(3, ZIP_LENGTH_ERROR).max(20, ZIP_LENGTH_ERROR),
      }),
    invoiceCountryOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    ordersEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) => emailSmartValidation(t).required(t('messages.required')),
      otherwise: (s: any) => emailSmartValidation(t).notRequired().nullable(),
    }),
    remindersEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) => emailSmartValidation(t).required(t('messages.required')),
      otherwise: (s: any) => emailSmartValidation(t).notRequired().nullable(),
    }),
    invoiceEmail: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) => emailSmartValidation(t).required(t('messages.required')),
      otherwise: (s: any) => emailSmartValidation(t).notRequired().nullable(),
    }),
    invoiceCcEmail: Yup.string().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: (s: any) =>
        emailSmartValidation(t)
          .nullable()
          .test('invoice-email-match', t('messages.ccInvoiceEmailIsTheSameAsInvoiceEmail'), function (value) {
            return this.parent.invoiceEmail !== value;
          }),
      otherwise: (s: any) => s.notRequired().nullable(),
    }),
    specialInvoiceNeeds: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when(['roleOption', 'isSpecialInvoiceNeedsOption'], {
        is: (roleOption: Option | null, isSpecialInvoiceNeedsOption: Option | null) =>
          roleOption?.value === Role.EPDOwner && isSpecialInvoiceNeedsOption?.value === YES_NO.YES,
        then: Yup.string().required(t('messages.required')),
      }),
    marketplace: Yup.array().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDDeveloper,
      then: Yup.array().min(1, t('messages.required')),
    }),
    gln: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when('invoiceCountryOption', {
        is: (invoiceCountryOption: Option | null) => {
          const countryName = countries.find((c) => c.id === invoiceCountryOption?.value)?.name;
          return countryName === 'Sweden';
        },
        then: (s: any) => s.matches(glnValidationRegex, 'Invalid GLN format'),
      }),
    preferredCurrencyOption: Yup.mixed().when('roleOption', {
      is: (roleOption: Option | null) => roleOption?.value === Role.EPDOwner,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    abn: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when(['countryOption', 'roleOption'], {
        is: (countryOption: Option | undefined, roleOption: Option | undefined) =>
          roleOption?.value === Role.EPDOwner &&
          countryOption &&
          AuLicenseesCountry.includes(countries.find((x) => x.id === countryOption.value)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
    gst: Yup.string()
      .matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR)
      .when(['countryOption', 'roleOption'], {
        is: (countryOption: Option | undefined, roleOption: Option | undefined) =>
          roleOption?.value === Role.EPDOwner &&
          countryOption &&
          AuLicenseesCountry.includes(countries.find((x) => x.id === countryOption.value)?.alpha2Code || ''),
        then: Yup.string().required(t('messages.required')),
      })
      .nullable(),
    regionOption: Yup.mixed().when('countryOption', {
      is: (countryOption: Option | null) =>
        getAvailableRegionsForCountryOptions(countryOption?.value || '', regions).length > 0,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    invoiceRegionOption: Yup.mixed().when(['invoiceCountryOption', 'roleOption'], {
      is: (invoiceCountryOption: Option | undefined, roleOption: Option | undefined) =>
        roleOption?.value === Role.EPDOwner &&
        getAvailableRegionsForCountryOptions(invoiceCountryOption?.value || '', regions).length > 0,
      then: Yup.object<Option>().required(t('messages.required')),
    }),
    invoiceReference: Yup.string().matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
  });

  const vatValidationProps = {
    enableTooltipError: true,
    supressErrors: false,
    shouldHighlightBorder: (error: string) => error !== t('messages.requiredVatInEU'),
    shouldShowBottomError: (error: string) => !!error,
    shouldShowTooltipError: (error: string) => error === t('messages.requiredVatInEU'),
    renderErrorMessage: (error: string) => {
      switch (error) {
        case 'vat-format-error':
          return (
            <span
              style={{
                color: theme.colors.systemStatusError,
                fontFamily: 'Inter',
                fontSize: '12px',
              }}
            >
              Format error: VAT number must start with a country code followed by numbers, using only letters and digits, no
              spaces.
            </span>
          );
        case 'vat-validation-error':
          return (
            <span
              style={{
                color: theme.colors.systemStatusError,
                fontFamily: 'Inter',
                fontSize: '12px',
              }}
            >
              VAT number is not registered. Verify here:{' '}
              <a
                href={VAT_VALIDATION_SERVICE_LINK}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'underline', color: 'inherit' }}
              >
                VIES VAT Checker
              </a>
              .
            </span>
          );
        default:
          return null;
      }
    },
  };

  const createCompany = async (
    rowValues: ExtendedCreateCompanyModel,
    formikHelpers: FormikHelpers<ExtendedCreateCompanyModel>
  ) => {
    const values: CreateCompanyModel = {
      ...rowValues,
      roleId: rowValues.roleOption?.value,
      companySize: rowValues.companySizeOption?.value as CompanySize | undefined,
      countryId: rowValues.countryOption?.value,
      regionId: rowValues.regionOption?.value ?? null,
      invoiceCountryId: rowValues.invoiceCountryOption?.value ?? null,
      invoiceRegionId: rowValues.invoiceRegionOption?.value ?? null,
      preferredCurrency: rowValues.preferredCurrencyOption?.value ?? CurrencyType.EUR,
      countries: rowValues.marketplace?.filter((c) => c.label !== 'Global').map((c) => ({ id: c.value, name: c.label })),
      isInvoiceUniquePurchaseOrderNumber: rowValues.isInvoiceUniquePurchaseOrderNumberOption?.value ?? null,
      isSpecialInvoiceNeeds: rowValues.isSpecialInvoiceNeedsOption?.value ?? null,
    };
    setServerError('');
    if (values.roleId === Role.EPDDeveloper) {
      values.isGlobalMarketplace = isGlobalMarketplaceSelected;
      values.companySize = CompanySize.Small;
      values.vat = null;
      values.gst = null;
      values.abn = null;
      values.invoiceOrganizationName = null;
      values.invoiceAddressCity = null;
      values.invoiceCountryId = null;
      values.invoiceAddressLine = null;
      values.invoiceAddressLineSecond = null;
      values.invoiceAddressZipCode = null;
      values.invoiceCcEmail = null;
      values.invoiceEmail = null;
      values.invoiceReference = null;
      values.isInvoiceUniquePurchaseOrderNumber = null;
      values.isSpecialInvoiceNeeds = null;
      values.ordersEmail = null;
      values.remindersEmail = null;
      values.specialInvoiceNeeds = null;
      values.invoiceRegionId = null;

      if (isGlobalMarketplaceSelected) {
        values.countries = [];
      }
    } else {
      values.isGlobalMarketplace = false;
      values.countries = [{ id: values.countryId, name: '', alpha2Code: '' }];
      if (values.isSpecialInvoiceNeeds === YES_NO.NO) {
        values.specialInvoiceNeeds = null;
      }
    }

    const { setSubmitting, setFormikState } = formikHelpers;

    try {
      const newCompanyId = await CompanyService.registerNewCompany(values);
      setSuccess(true);

      changeCompany?.(newCompanyId || '');
      await AuthService.refreshUser();
    } catch (e) {
      const errorMessage = PoorMansError(e);

      const duplicate = errorMessage ? duplicateErrorMap[errorMessage] : undefined;
      if (duplicate) {
        const { field, toastLabel, uiMessage } = duplicate;
        setFormikState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [field]: uiMessage },
          touched: { ...prev.touched, [field]: true },
        }));
        setServerError(undefined);
        toast.error(orgAlreadyExistsToastTemplate(toastLabel));
        return;
      }

      setServerError(errorMessage ?? t('errors.wrong'));
    } finally {
      setSubmitting(false);
    }
  };

  const orgAlreadyExistsToastTemplate = (label: string) => {
    return (
      <>
        <div>
          <OrgAlreadyExistsErrorNotificationTitle>
            The organisation already exists in our system.
          </OrgAlreadyExistsErrorNotificationTitle>
          <OrgAlreadyExistsErrorNotificationParagraph>
            Use a different {label} to create a new organisation, or request access to join the existing one.
          </OrgAlreadyExistsErrorNotificationParagraph>
          <OrgAlreadyExistsErrorNotificationParagraph>
            If you need assistance, contact us at{' '}
            <HyperLink href="mailto:support@environdec.com">support@environdec.com</HyperLink>
          </OrgAlreadyExistsErrorNotificationParagraph>
        </div>
      </>
    );
  };

  const handleOrganizationCountryChange = (selectedOption: Option, setFieldValue: any) => {
    setFieldValue('regionOption', null);
    return selectedOption;
  };

  const handleInvoiceCountryChange = (selectedOption: Option, setFieldValue: any) => {
    setFieldValue('invoiceRegionOption', null);

    if (!eurCurrencyCountries.data) return;
    const country = countries.find((c) => c.id === selectedOption.value);
    if (!country) return;
    const countryCode = country.alpha2Code;
    if (!countryCode) {
      setFieldValue('preferredCurrencyOption', null);
      return;
    }

    const options = calculateCurrencyOptions(
      countryCode,
      eurCurrencyCountries.data.map((c) => c.value)
    );

    setFieldValue('preferredCurrencyOption', options[0]);

    return selectedOption;
  };

  const handleChangeMarketplace = (options: Option[]) => {
    const globalOption = options?.find((option) => option.label === 'Global');
    if (globalOption) {
      setIsGlobalMarketplaceSelected(true);
      return [globalOption];
    } else {
      setIsGlobalMarketplaceSelected(false);
      return options;
    }
  };

  const isSpecialMarginNeeded = (countries: CountryModel[], values: ExtendedCreateCompanyModel) => {
    const countryId = values.countryOption?.value || '';

    return (
      AuLicenseesCountry.includes(countries.find((x) => x.id === countryId)?.alpha2Code || '') ||
      values.roleOption?.value !== Role.EPDOwner ||
      getAvailableRegionsForCountryOptions(countryId, regions).length > 0
    );
  };

  const setFormikFieldIfDifferent = <T extends Record<string, any>>(
    values: T,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    field: keyof T,
    nextValue: any
  ) => {
    if (values[field] !== nextValue) {
      setFieldValue(field as string, nextValue, false);
    }
  };

  const CopyEmailToInvoiceEmail = () => {
    const { values, setFieldValue, setFieldTouched, validateForm } = useFormikContext<typeof createInitialValues>();

    useEffect(() => {
      if (!values.isOnlyEmail) return;

      setFormikFieldIfDifferent(values, setFieldValue, 'invoiceEmail', values.organizationEmail);
      setFormikFieldIfDifferent(values, setFieldValue, 'ordersEmail', values.organizationEmail);
      setFormikFieldIfDifferent(values, setFieldValue, 'remindersEmail', values.organizationEmail);

      setFieldTouched('invoiceEmail', true, false);
      setFieldTouched('ordersEmail', true, false);
      setFieldTouched('remindersEmail', true, false);
      validateForm();
    }, [
      values.isOnlyEmail,
      values.organizationEmail,

      values.invoiceEmail,
      values.ordersEmail,
      values.remindersEmail,

      setFieldValue,
      setFieldTouched,
    ]);

    return null;
  };

  const CopyAddressToInvoiceAddress = () => {
    const { values, setFieldValue, setFieldTouched, validateForm } = useFormikContext<typeof createInitialValues>();

    useEffect(() => {
      if (!values.isCompanyAddressUsedForInvoice) return;

      setFormikFieldIfDifferent(values, setFieldValue, 'invoiceAddressCity', values.addressCity);
      setFormikFieldIfDifferent(values, setFieldValue, 'invoiceAddressLine', values.addressLine);
      setFormikFieldIfDifferent(values, setFieldValue, 'invoiceAddressLineSecond', values.addressLineSecond);
      setFormikFieldIfDifferent(values, setFieldValue, 'invoiceAddressZipCode', values.addressZipCode);

      setFieldTouched('invoiceAddressZipCode', true, false);

      const orgCountryValue = values.countryOption?.value ?? null;
      const invoiceCountryValue = values.invoiceCountryOption?.value ?? null;
      if (orgCountryValue !== invoiceCountryValue) {
        setFieldValue('invoiceCountryOption', values.countryOption, false);
      }

      const orgRegionValue = values.regionOption?.value ?? null;
      const invoiceRegionValue = values.invoiceRegionOption?.value ?? null;
      if (orgRegionValue !== invoiceRegionValue) {
        setFieldValue('invoiceRegionOption', values.regionOption, false);
      }

      const country = countries.find((c) => c.id === values.countryOption?.value);
      const countryCode = country?.alpha2Code;

      if (eurCurrencyCountries.data && countryCode) {
        const options = calculateCurrencyOptions(
          countryCode,
          eurCurrencyCountries.data.map((c) => c.value)
        );

        if (values.preferredCurrencyOption?.value !== options[0]?.value) {
          setFieldValue('preferredCurrencyOption', options[0], false);
        }
      }

      validateForm();
    }, [
      values.isCompanyAddressUsedForInvoice,

      values.countryOption,
      values.regionOption,
      values.addressZipCode,
      values.addressCity,
      values.addressLine,
      values.addressLineSecond,

      eurCurrencyCountries.data,
      countries,

      setFieldValue,
      setFieldTouched,
      values.invoiceCountryOption,
      values.invoiceRegionOption,
      values.invoiceAddressZipCode,
      values.invoiceAddressCity,
      values.invoiceAddressLine,
      values.invoiceAddressLineSecond,
      values.preferredCurrencyOption,
    ]);

    return null;
  };

  useEffect(() => {
    async function fetch() {
      const result = await CountriesService.getCountries();
      const emptyCountry: CountryModel = { id: undefined, name: '', alpha2Code: '' };
      setCountries([emptyCountry].concat(result));
      setCountriesDs(
        [GLOBAL_OPTION].concat(
          result.map((country) => {
            return { value: country.id, label: country.name } as Option;
          })
        )
      );
      const regionsResult = await CountriesService.getCountryRegions();
      setRegions(regionsResult);
    }
    fetch();
  }, []);

  useEffect(() => {
    if (success && licensee) {
      setShowCreatedLicenseeCompanyDialog(true);
    }
  }, [licensee]);

  return (
    <Container style={{ marginTop: 0, paddingLeft: 0 }}>
      <Text dangerouslySetInnerHTML={{ __html: t('organization.register.description') }} />
      <Formik
        initialValues={createInitialValues}
        validationSchema={createSchema}
        onSubmit={async (values, helpers) => {
          await createCompany(values, helpers);
        }}
      >
        {({ isSubmitting, isValid, dirty, values, errors, setFieldValue }) => (
          <RegisterCompanyForm mode={FormMode.Inline}>
            <Text style={{ fontWeight: 'bold', marginTop: 30 }}> {t('organization.register.general')}</Text>
            <FieldPanel>
              <Grid>
                <StyledReactSelect
                  required
                  label={t('organization.type')}
                  name="roleOption"
                  styles={registerCompanySelectStyles(theme)}
                  placeholder={t('placeholder.select')}
                  options={companyTypes}
                  enableTooltipError
                  supressErrors
                  components={{ DropdownIndicator }}
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('organization.name')}
                  name="name"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors={false}
                  trim
                  {...allowedSymbolsTextInputPropsForName}
                />
              </Grid>

              {values.roleOption?.value === Role.EPDOwner && (
                <>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('organizationSize')}
                      name="companySizeOption"
                      placeholder={t('placeholder.select')}
                      styles={registerCompanySelectStyles(theme)}
                      options={Object.values(CompanySize)
                        .filter((v) => v !== CompanySize.NotApplicable)
                        .map((x) => ({ value: x, label: x } as Option))}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('website')}
                      name="website"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForWebsite}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      name="organizationEmail"
                      label={t('organizationEmail')}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                    <CheckBoxGroup>
                      <Field type="checkbox" name="isOnlyEmail" id="isOnlyEmailId" />
                      <LabelWithIcon>
                        <label style={{ marginRight: '0.25rem' }} htmlFor="isOnlyEmailId">
                          {t(`organization.register.isOnlyEmail`)}
                        </label>
                        <TooltipHelpIcon
                          content={
                            'When selected, this email address will be automatically copied to invoicing-related email fields in "Your invoice information" block below. The fields will be locked and cannot be edited unless you uncheck this box.'
                          }
                        />
                      </LabelWithIcon>
                    </CheckBoxGroup>
                  </Grid>
                </>
              )}

              {values.roleOption?.value === Role.EPDDeveloper && (
                <>
                  <Grid>
                    <StyledReactSelect
                      placeholder={t('placeholder.select')}
                      label={t('organization.register.marketplace')}
                      name="marketplace"
                      isMulti
                      styles={registerCompanySelectStyles(theme)}
                      options={countriesDs}
                      onChange={handleChangeMarketplace}
                      isClearable
                      required
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      label={t('website')}
                      tooltip={"Your website will appear on the 'LCA Consultants' page at environdec.com"}
                      tooltipPosition="title"
                      name="website"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForWebsite}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      name="organizationEmail"
                      label={t('organizationEmail')}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                  </Grid>
                </>
              )}
            </FieldPanel>

            <div className="w-full">
              <GrayHR />
            </div>

            {/* Organization address */}
            <Text style={{ fontWeight: 'bold' }}>Organisation address</Text>
            <FieldPanel>
              <Grid>
                <StyledReactSelect
                  placeholder={t('placeholder.select')}
                  required
                  label={t('country')}
                  name="countryOption"
                  onChange={(value: Option) => handleOrganizationCountryChange(value, setFieldValue)}
                  styles={registerCompanySelectStyles(theme)}
                  options={countries.map((c) => {
                    return { value: c.id, label: c.name } as Option;
                  })}
                  enableTooltipError
                  supressErrors
                  components={{ DropdownIndicator }}
                />
              </Grid>
              <AvailableCountryRegions
                label="Region"
                countryId={values.countryOption?.value ?? null}
                name="regionOption"
                regions={regions}
                styles={registerCompanySelectStyles(theme)}
                enableTooltipError
                supressErrors
              />
              <Grid>
                <TextInput
                  required
                  label={t('organizationAddressZipCode')}
                  name="addressZipCode"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors={false}
                  trim
                  {...allowedSymbolsTextInputPropsForZip}
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('organizationAddressCity')}
                  name="addressCity"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors={false}
                  {...allowedSymbolsTextInputProps}
                />
              </Grid>
              <Grid>
                <TextInput
                  required
                  label={t('placeholder.address.line1')}
                  name="addressLine"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors={false}
                  {...allowedSymbolsTextInputProps}
                />
                {values.roleOption?.value === Role.EPDOwner && (
                  <>
                    <CheckBoxGroup>
                      <Field type="checkbox" name="isCompanyAddressUsedForInvoice" id="isCompanyAddressUsedForInvoiceId" />
                      <LabelWithIcon>
                        <label style={{ marginRight: '0.25rem' }} htmlFor="isCompanyAddressUsedForInvoiceId">
                          {t(`organization.register.useCompanyAddressForInvoicing`)}
                        </label>
                        <TooltipHelpIcon
                          content={
                            'When selected, the organisation address will be automatically copied to corresponding invoicing address fields. The fields will be locked and cannot be edited unless you uncheck this box.'
                          }
                        />
                      </LabelWithIcon>
                    </CheckBoxGroup>
                  </>
                )}
              </Grid>
              <Grid style={isSpecialMarginNeeded(countries, values) ? {} : { marginTop: '-20px' }}>
                <TextInput
                  label={t('placeholder.address.line2')}
                  name="addressLineSecond"
                  placeholder={t('placeholder.typeHere')}
                  enableTooltipError
                  supressErrors={false}
                  {...allowedSymbolsTextInputProps}
                />
              </Grid>
              {values.roleOption?.value === Role.EPDOwner &&
                AuLicenseesCountry.includes(
                  countries.find((x) => x.id === values.countryOption?.value)?.alpha2Code || ''
                ) && (
                  <>
                    <Grid>
                      <TextInput
                        required
                        label={t('abn')}
                        name="abn"
                        placeholder={t('placeholder.typeHere')}
                        enableTooltipError
                        supressErrors={false}
                        {...allowedSymbolsTextInputProps}
                      />
                    </Grid>
                    <Grid>
                      <TextInput
                        required
                        label={t('gst')}
                        name="gst"
                        placeholder={t('placeholder.typeHere')}
                        enableTooltipError
                        supressErrors={false}
                        {...allowedSymbolsTextInputProps}
                      />
                    </Grid>
                  </>
                )}
            </FieldPanel>

            {values.roleOption?.value === Role.EPDOwner && (
              <>
                <div className="w-full">
                  <GrayHR />
                </div>

                <Text style={{ fontWeight: 'bold' }}>{t('invoiceInfo.title')}</Text>
                <FieldPanel>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceOrganizationName')}
                      name="invoiceOrganizationName"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputProps}
                    />
                  </Grid>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('invoiceAddressCountry')}
                      name="invoiceCountryOption"
                      onChange={(value: Option) => handleInvoiceCountryChange(value, setFieldValue)}
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={countries.map((c) => {
                        return { value: c.id, label: c.name } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      isDisabled={values.isCompanyAddressUsedForInvoice}
                    />
                  </Grid>
                  <AvailableCountryRegions
                    label="Invoice Country Region"
                    countryId={values.invoiceCountryOption?.value ?? null}
                    name="invoiceRegionOption"
                    regions={regions}
                    styles={registerCompanySelectStyles(theme)}
                    enableTooltipError
                    supressErrors
                    isDisabled={values.isCompanyAddressUsedForInvoice}
                  />
                  <Grid>
                    <AvailableCountryCurrencies
                      countries={countries}
                      styles={registerCompanySelectStyles(theme)}
                      tooltip={!values.invoiceCountryOption ? t('invoicePreferredCurrencyTooltip') : ''}
                      enableTooltipError
                      supressErrors
                      isRequired={true}
                      isDisabled={!values.invoiceCountryOption || values.isCompanyAddressUsedForInvoice}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressZipCode')}
                      name="invoiceAddressZipCode"
                      disabled={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForZip}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressCity')}
                      name="invoiceAddressCity"
                      disabled={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      {...allowedSymbolsTextInputProps}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceAddressLine')}
                      name="invoiceAddressLine"
                      disabled={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      {...allowedSymbolsTextInputProps}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      label={t('invoiceAddressLineSecond')}
                      name="invoiceAddressLineSecond"
                      disabled={values.isCompanyAddressUsedForInvoice}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      {...allowedSymbolsTextInputProps}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoicingEmail')}
                      name="invoiceEmail"
                      disabled={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      enableTooltipError
                      label={t('invoiceCcEmail')}
                      name="invoiceCcEmail"
                      placeholder={t('placeholder.typeHere')}
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceOrderingEmail')}
                      name="ordersEmail"
                      disabled={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                  </Grid>
                  <Grid>
                    <TextInput
                      required
                      label={t('invoiceRemindingEmail')}
                      name="remindersEmail"
                      disabled={values.isOnlyEmail}
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      trim
                      {...allowedSymbolsTextInputPropsForEmail}
                    />
                  </Grid>
                  {(EUCountries.includes(
                    countries.find((x) => x.id === values.invoiceCountryOption?.value)?.alpha2Code || ''
                  ) ||
                    EUCountries.includes(
                      regions.find((r) => r.id === values.invoiceRegionOption?.value)?.alpha2Code || ''
                    )) && (
                    <Grid>
                      <TextInput
                        label={t('vat')}
                        name="vat"
                        required={true}
                        placeholder={t('placeholder.typeHere')}
                        {...vatValidationProps}
                      />
                    </Grid>
                  )}

                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('isSpecialInvoice')}
                      name="isSpecialInvoiceNeedsOption"
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={YesNoDataSource.map((x) => {
                        return { value: x.value, label: x.label } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  <Grid>
                    <StyledReactSelect
                      required
                      label={t('isUniqueInvoicePurchaseOrderNumberRequired')}
                      name="isInvoiceUniquePurchaseOrderNumberOption"
                      styles={registerCompanySelectStyles(theme)}
                      placeholder={t('placeholder.select')}
                      options={YesNoDataSource.map((x) => {
                        return { value: x.value, label: x.label } as Option;
                      })}
                      enableTooltipError
                      supressErrors
                      components={{ DropdownIndicator }}
                    />
                  </Grid>
                  {values.isSpecialInvoiceNeedsOption?.value === YES_NO.YES && (
                    <>
                      <Grid>
                        <TextInput
                          required
                          label={t('organization.register.specialInvoiceNeeds')}
                          name="specialInvoiceNeeds"
                          placeholder={t('placeholder.typeHere')}
                          enableTooltipError
                          supressErrors={false}
                          {...allowedSymbolsTextInputProps}
                        />
                      </Grid>
                    </>
                  )}
                  {values.invoiceCountryOption?.value &&
                    countries.find((c) => c.id === values.invoiceCountryOption?.value)?.name === 'Sweden' && (
                      <>
                        <Grid>
                          <TextInput
                            label={t('gln')}
                            name="gln"
                            placeholder={t('placeholder.typeHere')}
                            enableTooltipError
                            supressErrors={false}
                            {...allowedSymbolsTextInputProps}
                          />
                        </Grid>
                      </>
                    )}
                  <Grid style={{ gridColumn: 'span 2' }}>
                    <LabelWithIcon>
                      <Label style={{ paddingBottom: 0, marginRight: '0.25rem' }}>{t('invoiceReference')}</Label>
                      <TooltipHelpIcon content={'Maximum 50 characters allowed'} />
                    </LabelWithIcon>
                    <TextAreaInput
                      maxLength={50}
                      name="invoiceReference"
                      placeholder={t('placeholder.typeHere')}
                      enableTooltipError
                      supressErrors={false}
                      {...allowedSymbolsTextInputProps}
                    />
                  </Grid>
                </FieldPanel>
              </>
            )}
            <ButtonGroup>
              <ButtonSmall type="submit" disabled={isSubmitting || success || !isValid || !dirty}>
                {isSubmitting ? t('organization.register.creating') : t('organization.register.create')}
              </ButtonSmall>
              {success && (
                <SuccessText>
                  <div>{t('organization.register.success')}</div>
                  <StyledLink to={EpdLinks.dashboard()}>{t('organization.register.goToDashboard')}</StyledLink>
                </SuccessText>
              )}
              {serverError && <ErrorText>{serverError}</ErrorText>}
            </ButtonGroup>
            <CopyEmailToInvoiceEmail />
            <CopyAddressToInvoiceAddress />
          </RegisterCompanyForm>
        )}
      </Formik>
      <br />
      <InformationalDialog
        isOpened={showCreatedLicenseeCompanyDialog && !!licensee?.localHomepage}
        header="Regional Licensee country is detected"
        onHide={() => setShowCreatedLicenseeCompanyDialog(false)}
      >
        Thank you for registering with EPD International. We have various Licensees around the world. You have selected a
        country located in the regional license holder {company?.address?.country}. The regional administrators will help you
        with all support regarding your EPD. To find the local support, please visit{' '}
        <LicenseeSiteLink href={normalizeUrl(licensee?.localHomepage)}>{licensee?.localHomepage}</LicenseeSiteLink>
      </InformationalDialog>
    </Container>
  );
};

export const RegisterCompanyForm = styled(StyledForm)`
  ${FieldPanel} {
    box-shadow: none;
    align-items: flex-start;
    padding-left: 0;
    padding-bottom: 0;
  }

  ${GrayHR} {
    margin: 30px 30px 40px 0;
  }

  input {
    ${(props) => props.theme.fonts.textSmall}
    color: rgba(21, 21, 21, 1);
    &::placeholder {
      color: ${(props) => props.theme.colors.primaryBlack70};
      opacity: 1;
    }

    &:disabled {
      color: rgba(156, 156, 156, 1);
      &::placeholder {
        color: rgba(156, 156, 156, 1);
        opacity: 1;
      }
    }
  }
  textarea {
    color: rgba(21, 21, 21, 1);
    &::placeholder {
      color: ${(props) => props.theme.colors.primaryBlack70};
      opacity: 1;
    }
    &:disabled {
      color: rgba(156, 156, 156, 1);
      &::placeholder {
        color: rgba(156, 156, 156, 1);
        opacity: 1;
      }
    }
  }
`;

const LicenseeSiteLink = styled(A)`
  color: ${(props) => props.theme.colors.primaryOrange};
`;

const Grid = styled.div`
  display: grid;

  ${StyledReactSelect} {
    margin: 0.5rem 0 1rem;
  }

  label {
    ${(props) => props.theme.fonts.textSmall}
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 50px;
  padding-right: 30px;
  min-height: 55px;

  ${ButtonSmall} {
    align-self: center;
    margin-right: 2rem;
    height: 48px;
    &:disabled {
      background: #696969;
      opacity: 1;
    }
  }

  ${SuccessText} {
    margin-top: 0;
    margin-bottom: 0;

    & div {
      margin-bottom: 1rem;
    }
  }
`;

const CheckBoxGroup = styled.div`
  display: flex;
  align-items: center;
  min-height: 20px;
  & .p-checkbox {
    width: 14px;
    height: 14px;
  }
  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
    border: 1px solid ${(props) => props.theme.colors.elementsButtonDisabled};
    border-radius: 0;
  }
  & .p-checkbox .p-checkbox-box.p-highlight {
    background: ${(props) => props.theme.colors.primaryOrange};
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  & .p-checkbox .p-checkbox-box.p-highlight:hover {
    background: ${(props) => props.theme.colors.primaryOrange};
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  && .p-checkbox-box.p-focus {
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }

  label {
    display: flex;
    margin-left: 0.5rem;
    ${(props) => props.theme.fonts.textSmall}
    color: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 0.875rem;
  }
`;

const OrgAlreadyExistsErrorNotificationTitle = styled.b`
  font-family: Inter;
  font-weight: 600;
  line-height: 20px;
`;

const OrgAlreadyExistsErrorNotificationParagraph = styled.p`
  font-family: Inter;
  font-weight: 400;
  line-height: 20px;
  opacity: 70%;
  margin-top: 10px;
  margin-bottom: 0px;
`;

const normalizeUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export default RegisterCompany;
